import React from 'react';
import * as CSS from 'csstype';
import styled from 'styled-components';
import { BriefDetails_StructuredBriefFragment } from '@99designs/apps/spa/pages/create/components/BriefDetailsList.generated';
import { Box, Divider, Paragraph, Stack, Tooltip } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces } from '@99designs/emu/settings';
import { Pill } from '@99designs/frontend-components/components/Pill';
import { RelativeTime } from '@99designs/frontend-components/components/RelativeTime';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { IconQuestionMark1, IconRight1 } from '@99designs/icons';
import AvatarCollection from './AvatarCollection';
import User from './User';

const Item = styled.div<{ disabled?: boolean }>`
    padding: ${spaces['1bu']} ${spaces['2bu']};
    margin: -2px 0;
    cursor: pointer;
    transition: background-color 0.15s ease-out;

    &:hover {
        background-color: ${colors.grey100};
    }

    ${mediaQuery.minimum('medium')} {
        padding: ${spaces['2bu']} ${spaces['4bu']};
    }

    ${({ disabled }) => disabled && 'pointer-events: none'};
    ${({ disabled }) => disabled && 'opacity: 0.25'};
    ${({ disabled }) => disabled && `background-color: ${colors.grey100}`};
`;

const Details = styled.div`
    width: 100%;
`;

const Icon = styled.div`
    width: 32px;
    height: 32px;
    margin-left: ${spaces['2bu']};
`;

const TooltipWrapper = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: calc(${spaces['2bu']} + ${spaces['0.5bu']});

    ${mediaQuery.minimum('medium')} {
        right: calc(${spaces['4bu']} + ${spaces['0.5bu']});
    }
`;

const ItemWrapper = styled.div`
    position: relative;
`;

interface Props {
    drafts: BriefDetails_StructuredBriefFragment[];
    heading: string;
    onClick: (brief: BriefDetails_StructuredBriefFragment) => void;
    designer: { profile: { certifications: string[] } } | null;
}

const BriefDetailsList = ({ drafts, onClick, heading, designer }: Props) => (
    <Box paddingY="2bu">
        <Box paddingX={['2bu', '4bu']} color={'grey800'}>
            <div className="heading heading--h4">{heading}</div>
        </Box>
        {drafts.map((draft, id) => {
            let disableDraft = false;
            let tooltipTitle = '';

            if (
                designer &&
                !draft.requiresCertifications.every((certification) =>
                    designer.profile.certifications.includes(certification)
                )
            ) {
                disableDraft = true;
                tooltipTitle = __(
                    'The selected designer is not certified for this type of project'
                );
            }
            if (draft.product?.__typename) {
                disableDraft = true;
                tooltipTitle = __('This project type is not available for direct invitations');
            }

            return (
                <ItemWrapper key={id}>
                    {id > 0 && (
                        <Box marginX={['2bu', '4bu']}>
                            <Divider />
                        </Box>
                    )}
                    <Item
                        onClick={() => onClick(draft)}
                        data-testid={`brief-item-${draft.id}`}
                        disabled={disableDraft}
                    >
                        <Box display="flex" flexDirection="row" alignItems="center">
                            <Details>
                                <BriefDetails {...draft} />
                            </Details>
                            <Icon>
                                {!disableDraft && <IconRight1 width="100%" height="100%" />}
                            </Icon>
                        </Box>
                    </Item>
                    {disableDraft && (
                        <TooltipWrapper>
                            <Tooltip title={tooltipTitle}>
                                <span data-testid={`hover-span-${draft.id}`}>
                                    <IconQuestionMark1 />
                                </span>
                            </Tooltip>
                        </TooltipWrapper>
                    )}
                </ItemWrapper>
            );
        })}
    </Box>
);

const ResponsiveRow = styled.div<{ justifyContent?: CSS.Property.JustifyContent }>`
    display: flex;
    flex-direction: column;
    align-items: baseline;

    > * {
        margin-top: ${spaces['0.25bu']};
        margin-left: ${spaces['0.25bu']};
    }

    ${mediaQuery.minimum('normal')} {
        flex-direction: row;
        justify-content: ${(props) => (props.justifyContent ? props.justifyContent : 'none')};
        align-items: center;
    }
`;

const BriefDetails = ({
    title,
    category,
    designers,
    createdAt,
}: BriefDetails_StructuredBriefFragment) => (
    <div data-testid="brief-details">
        <Stack space="0.25bu">
            <ResponsiveRow>
                <div title={title}>{title}</div>
                {category && <Pill>{category.title}</Pill>}
            </ResponsiveRow>
            <ResponsiveRow justifyContent="space-between">
                {designers.length === 1 && <User user={designers[0]} size={'extra-small'} />}
                {designers.length > 1 && (
                    <AvatarCollection users={designers} size={'extra-small'} limit={5} />
                )}
                <Paragraph as="div" color="grey700" size="small">
                    <FormattedMessage
                        message={__('Created {time}')}
                        values={{ time: <RelativeTime date={createdAt} /> }}
                    />
                </Paragraph>
            </ResponsiveRow>
        </Stack>
    </div>
);

export default BriefDetailsList;
